import { useState, useMemo } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Target, Copy, Plus, ChevronLeft, ChevronRight, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import { FinancePageLayout, MoneyDisplay, BusinessUnitSelector } from '@/components/finance';
import type { BusinessUnit } from '@/components/finance/BusinessUnitSelector';
import { useBudgetEntries, useUpsertBudgetEntry, useCopyBudgetFromYear } from '@/hooks/finance/useBudget';
import { useChartOfAccounts } from '@/hooks/finance/useChartOfAccounts';

const MONTHS = [
  'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
];

export default function Budget() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [country, setCountry] = useState<BusinessUnit>('all');
  const [editingCell, setEditingCell] = useState<{ accountCode: string; month: number } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newEntryData, setNewEntryData] = useState({ accountCode: '', month: 1, amount: '' });

  const { data: budgetEntries = [], isLoading } = useBudgetEntries(year, country === 'all' ? undefined : country);
  const { data: accounts = [] } = useChartOfAccounts();
  const upsertBudget = useUpsertBudgetEntry();
  const copyBudget = useCopyBudgetFromYear();

  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  // Group budget by account
  const budgetByAccount = useMemo(() => {
    const grouped: Record<string, {
      account: { code: string; name: string; type: string; category: string | null };
      months: Record<number, number>;
      total: number;
    }> = {};

    budgetEntries.forEach(entry => {
      const code = entry.account_code || 'unknown';
      if (!grouped[code]) {
        grouped[code] = {
          account: entry.account || { code, name: code, type: 'expense', category: null },
          months: {},
          total: 0,
        };
      }
      grouped[code].months[entry.month] = Number(entry.planned_amount);
      grouped[code].total += Number(entry.planned_amount);
    });

    return grouped;
  }, [budgetEntries]);

  // Group accounts by type/category
  const groupedAccounts = useMemo(() => {
    const expenseAccounts = accounts.filter(a => a.type !== 'revenue');
    const groups: Record<string, typeof expenseAccounts> = {};
    
    expenseAccounts.forEach(acc => {
      const category = acc.category || 'Outros';
      if (!groups[category]) groups[category] = [];
      groups[category].push(acc);
    });

    return groups;
  }, [accounts]);

  const handleCellClick = (accountCode: string, month: number) => {
    const currentValue = budgetByAccount[accountCode]?.months[month] || 0;
    setEditValue(currentValue.toString());
    setEditingCell({ accountCode, month });
  };

  const handleSaveCell = async () => {
    if (!editingCell) return;

    const amount = parseFloat(editValue) || 0;
    try {
      await upsertBudget.mutateAsync({
        account_code: editingCell.accountCode,
        country: country === 'all' ? 'all' : country,
        year,
        month: editingCell.month,
        planned_amount: amount,
        currency: country === 'br' ? 'BRL' : 'USD',
      });
      setEditingCell(null);
    } catch (error) {
      toast.error('Erro ao salvar');
      console.error(error);
    }
  };

  const handleCopyFromYear = async () => {
    try {
      const result = await copyBudget.mutateAsync({
        sourceYear: year - 1,
        targetYear: year,
        country: country === 'all' ? undefined : country,
      });
      toast.success(`${result.copied} entradas copiadas de ${year - 1}`);
      setIsCopyDialogOpen(false);
    } catch (error: any) {
      toast.error(error.message || 'Erro ao copiar orçamento');
    }
  };

  const handleAddEntry = async () => {
    if (!newEntryData.accountCode || !newEntryData.amount) {
      toast.error('Preencha todos os campos');
      return;
    }

    try {
      await upsertBudget.mutateAsync({
        account_code: newEntryData.accountCode,
        country: country === 'all' ? 'all' : country,
        year,
        month: newEntryData.month,
        planned_amount: parseFloat(newEntryData.amount),
        currency: country === 'br' ? 'BRL' : 'USD',
      });
      toast.success('Orçamento adicionado');
      setIsAddDialogOpen(false);
      setNewEntryData({ accountCode: '', month: 1, amount: '' });
    } catch (error) {
      toast.error('Erro ao adicionar');
      console.error(error);
    }
  };

  // Calculate totals
  const monthTotals = useMemo(() => {
    const totals: Record<number, number> = {};
    Object.values(budgetByAccount).forEach(({ months }) => {
      Object.entries(months).forEach(([month, amount]) => {
        totals[Number(month)] = (totals[Number(month)] || 0) + amount;
      });
    });
    return totals;
  }, [budgetByAccount]);

  const grandTotal = Object.values(monthTotals).reduce((sum, v) => sum + v, 0);

  return (
    <FinancePageLayout>
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <h1 className="text-2xl font-bold tracking-tight">Orçamento {year}</h1>
        <div className="flex gap-2">
            <Button variant="outline" onClick={() => setIsCopyDialogOpen(true)}>
              <Copy className="h-4 w-4 mr-2" />
              Copiar de {year - 1}
            </Button>
            <Button onClick={() => setIsAddDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Adicionar
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="space-y-2">
                <Label>Ano</Label>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="icon" onClick={() => setYear(y => y - 1)}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
                    <SelectTrigger className="w-[100px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((y) => (
                        <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="icon" onClick={() => setYear(y => y + 1)}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>País</Label>
                <BusinessUnitSelector value={country} onChange={setCountry} includeAll />
              </div>
              <div className="flex-1" />
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Total Anual</p>
                <p className="text-2xl font-bold">
                  <MoneyDisplay value={grandTotal} currency={country === 'br' ? 'BRL' : 'USD'} />
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Budget Grid */}
        <Card>
          <CardContent className="pt-6 overflow-x-auto">
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">Carregando...</div>
            ) : Object.keys(budgetByAccount).length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Target className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Nenhum orçamento definido para {year}</p>
                <Button className="mt-4" onClick={() => setIsAddDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Adicionar Primeira Entrada
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 bg-background min-w-[200px]">Conta</TableHead>
                    {MONTHS.map((m, i) => (
                      <TableHead key={i} className="text-center min-w-[100px]">{m}</TableHead>
                    ))}
                    <TableHead className="text-right min-w-[120px]">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(budgetByAccount)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([code, { account, months, total }]) => (
                      <TableRow key={code}>
                        <TableCell className="sticky left-0 bg-background font-medium">
                          <div>
                            <span className="text-muted-foreground text-xs">{code}</span>
                            <span className="ml-2">{account.name}</span>
                          </div>
                        </TableCell>
                        {MONTHS.map((_, monthIdx) => {
                          const month = monthIdx + 1;
                          const value = months[month] || 0;
                          const isEditing = editingCell?.accountCode === code && editingCell?.month === month;

                          return (
                            <TableCell key={month} className="text-center p-1">
                              {isEditing ? (
                                <Input
                                  type="number"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onBlur={handleSaveCell}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveCell();
                                    if (e.key === 'Escape') setEditingCell(null);
                                  }}
                                  className="w-20 text-center h-8"
                                  autoFocus
                                />
                              ) : (
                                <button
                                  onClick={() => handleCellClick(code, month)}
                                  className="w-full h-8 hover:bg-muted/50 rounded px-2 text-sm"
                                >
                                  {value > 0 ? value.toLocaleString('pt-BR') : '-'}
                                </button>
                              )}
                            </TableCell>
                          );
                        })}
                        <TableCell className="text-right font-semibold">
                          <MoneyDisplay value={total} currency={country === 'br' ? 'BRL' : 'USD'} />
                        </TableCell>
                      </TableRow>
                    ))}
                  {/* Totals Row */}
                  <TableRow className="bg-muted/50 font-bold">
                    <TableCell className="sticky left-0 bg-muted/50">TOTAL</TableCell>
                    {MONTHS.map((_, monthIdx) => (
                      <TableCell key={monthIdx} className="text-center">
                        {(monthTotals[monthIdx + 1] || 0).toLocaleString('pt-BR')}
                      </TableCell>
                    ))}
                    <TableCell className="text-right">
                      <MoneyDisplay value={grandTotal} currency={country === 'br' ? 'BRL' : 'USD'} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Copy Dialog */}
        <AlertDialog open={isCopyDialogOpen} onOpenChange={setIsCopyDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Copiar Orçamento</AlertDialogTitle>
              <AlertDialogDescription>
                Isso copiará todos os valores de orçamento de {year - 1} para {year}.
                Valores existentes serão sobrescritos.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleCopyFromYear} disabled={copyBudget.isPending}>
                {copyBudget.isPending ? 'Copiando...' : 'Copiar'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Add Entry Dialog */}
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Adicionar Entrada de Orçamento</DialogTitle>
              <DialogDescription>
                Defina o valor orçado para uma conta específica.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Conta</Label>
                <Select value={newEntryData.accountCode} onValueChange={(v) => setNewEntryData(d => ({ ...d, accountCode: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma conta" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(groupedAccounts).map(([category, accs]) => (
                      <div key={category}>
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase">
                          {category}
                        </div>
                        {accs.map((acc) => (
                          <SelectItem key={acc.code} value={acc.code}>
                            {acc.code} - {acc.name}
                          </SelectItem>
                        ))}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Mês</Label>
                <Select value={newEntryData.month.toString()} onValueChange={(v) => setNewEntryData(d => ({ ...d, month: parseInt(v) }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m, i) => (
                      <SelectItem key={i} value={(i + 1).toString()}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Valor ({country === 'br' ? 'BRL' : 'USD'})</Label>
                <Input
                  type="number"
                  value={newEntryData.amount}
                  onChange={(e) => setNewEntryData(d => ({ ...d, amount: e.target.value }))}
                  placeholder="0.00"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleAddEntry} disabled={upsertBudget.isPending}>
                {upsertBudget.isPending ? 'Salvando...' : 'Salvar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </FinancePageLayout>
  );
}
